import React, { useState, useEffect } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { lumi } from '../lib/lumi';

interface EditableTextProps {
  pageId: string;
  elementId: string;
  defaultContent: string;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  multiline?: boolean;
}

export function EditableText({
  pageId,
  elementId,
  defaultContent,
  className = '',
  as: Component = 'span',
  multiline = false
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(defaultContent);
  const [editValue, setEditValue] = useState(defaultContent);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState('24px');
  const [fontColor, setFontColor] = useState('#DC143C');
  const [fontFamily, setFontFamily] = useState('inherit');

  useEffect(() => {
    const checkAdminAndLoadContent = async () => {
      try {
        // Check if user is admin
        const user = lumi.auth.user;
        setIsAdmin(user?.userRole === 'ADMIN');

        // Load saved content from database
        const result = await lumi.entities.editableContent.list({
          filter: { pageId, elementId },
          limit: 1
        });

        if (result.list && result.list.length > 0) {
          const saved = result.list[0];
          setContent(saved.content);
          setEditValue(saved.content);
          if (saved.fontSize) setFontSize(saved.fontSize);
          if (saved.fontColor) setFontColor(saved.fontColor);
          if (saved.fontFamily) setFontFamily(saved.fontFamily);
        }
      } catch (error) {
        console.error('Error loading editable content:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndLoadContent();
  }, [pageId, elementId]);

  const handleSave = async () => {
    try {
      // Check if content exists
      const existing = await lumi.entities.editableContent.list({
        filter: { pageId, elementId },
        limit: 1
      });

      const updateData = {
        content: editValue,
        fontSize,
        fontColor,
        fontFamily,
        updatedAt: new Date(),
        updatedBy: lumi.auth.user?.userId || 'admin'
      };

      if (existing.list && existing.list.length > 0) {
        // Update existing
        await lumi.entities.editableContent.update(existing.list[0]._id, updateData);
      } else {
        // Create new
        await lumi.entities.editableContent.create({
          pageId,
          elementId,
          ...updateData,
          createdAt: new Date()
        });
      }

      setContent(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving editable content:', error);
      alert('Failed to save changes');
    }
  };

  const handleCancel = () => {
    setEditValue(content);
    setIsEditing(false);
  };

  if (loading) {
    return <Component className={className}>{defaultContent}</Component>;
  }

  return (
    <div className="relative group inline-block w-full">
      {!isEditing ?
      <>
          <Component
          className={className}
          style={{
            fontSize,
            color: fontColor,
            fontFamily
          }}>

            {content}
          </Component>
          {isAdmin &&
        <button
          onClick={() => setIsEditing(true)}
          className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#DC143C] text-white p-1.5 rounded hover:bg-red-600"
          title="Edit text">

              <Edit2 size={14} />
            </button>
        }
        </> :

      <div className="space-y-3 w-full bg-[#1A1A1A] p-4 rounded-lg border-2 border-[#DC143C]">
          {/* Text Input */}
          <div className="flex items-start gap-2">
            {multiline ?
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="flex-1 bg-white text-black border-2 border-[#DC143C] rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC143C]/50"
            rows={4}
            autoFocus /> :


          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="flex-1 bg-white text-black border-2 border-[#DC143C] rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#DC143C]/50"
            autoFocus />

          }
          </div>

          {/* Styling Controls */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-300 mb-1">Font Size</label>
              <select
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
              className="w-full bg-[#0A0A0A] text-white border border-[#DC143C]/30 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#DC143C]">

                <option value="12px">12px</option>
                <option value="14px">14px</option>
                <option value="16px">16px</option>
                <option value="18px">18px</option>
                <option value="20px">20px</option>
                <option value="24px">24px</option>
                <option value="28px">28px</option>
                <option value="32px">32px</option>
                <option value="36px">36px</option>
                <option value="48px">48px</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-1">Font Color</label>
              <input
              type="color"
              value={fontColor}
              onChange={(e) => setFontColor(e.target.value)}
              className="w-full h-[34px] bg-[#0A0A0A] border border-[#DC143C]/30 rounded cursor-pointer" />

            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-1">Font Family</label>
              <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="w-full bg-[#0A0A0A] text-white border border-[#DC143C]/30 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-[#DC143C]">

                <option value="inherit">Default</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Courier New', monospace">Courier New</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="Verdana, sans-serif">Verdana</option>
                <option value="'Comic Sans MS', cursive">Comic Sans</option>
                <option value="Impact, sans-serif">Impact</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
            onClick={handleSave}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-semibold flex items-center justify-center gap-2"
            title="Save">

              <Check size={16} />
              Save Changes
            </button>
            <button
            onClick={handleCancel}
            className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
            title="Cancel">

              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      }
    </div>);

}